import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import eventEmitter, { EVENT_TYPES } from './ContextEvents';
import { HR_EMPLOYEES_REFRESH_EVENT } from '../../pages/ressources-humaines/hrRefreshEvents';
import { employeeService } from '../../shared/utils/serviceProvider';
import i18n from '../../i18n';

const em = (key, opts) => i18n.t(`serviceMessages.employees.${key}`, opts);

const EmployeesContext = createContext();

/** Normalise API RH (`nom`/`prenom`/`mat`/`status`) et ancien format (`first_name`/`active`) */
export function formatEmployeeRecord(emp) {
  if (!emp) return null;
  const firstName = emp.firstName || emp.first_name || emp.prenom || '';
  const lastName = emp.lastName || emp.last_name || emp.nom || '';
  const matricule = String(emp.mat || emp.employeeId || emp.employee_id || '').trim();
  const nameFromParts = `${firstName} ${lastName}`.trim();
  const name = nameFromParts || emp.name || emp.email || em('default_name');
  const statusRaw = String(emp.status || '').toLowerCase();
  const isActive =
    emp.active === 1 ||
    emp.active === true ||
    statusRaw === 'active' ||
    statusRaw === 'actif';
  return {
    id: emp.id,
    firstName,
    lastName,
    email: emp.email || '',
    phone: emp.phone || emp.telephone || '',
    address: emp.address || emp.adresse || '',
    city: emp.city || emp.ville || '',
    position: emp.position || emp.fonction || emp.posteOccupe || '',
    employeeId: matricule || emp.employeeId || emp.employee_id || String(emp.id),
    matricule: matricule || String(emp.id),
    workType: emp.workType || emp.work_type || emp.employee_type || 'full-time',
    employeeType: emp.employee_type || emp.work_type || emp.workType || 'full_time',
    status: emp.status || (isActive ? 'active' : 'inactive'),
    salary: emp.salary || emp.salaire_base || 0,
    department: emp.department || emp.affectation || '',
    hireDate: emp.hireDate || emp.dateRecrutement || (emp.created_at ? emp.created_at.split('T')[0] : ''),
    active: isActive,
    isActive,
    name,
    role: emp.fonction || emp.posteOccupe || emp.position || ''
  };
}

export const useEmployees = () => {
  const context = useContext(EmployeesContext);
  if (!context) {
    throw new Error(em('hook_outside_provider'));
  }
  return context;
};

export const EmployeesProvider = ({ children }) => {
  // Initialize employees from database via API or empty array
  const [employees, setEmployees] = useState([]);
  
  // Load employees from database on mount
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        // Get employees using the service provider
        const data = await employeeService.getEmployees();
        if (Array.isArray(data)) {
          const formattedEmployees = data.map(formatEmployeeRecord).filter(Boolean);
          setEmployees(formattedEmployees);
          console.log(`Loaded ${data.length} employees from database`);
        } else {
          console.log('No employees found or invalid data format.');
          setEmployees([]);
        }
      } catch (error) {
        console.error('Failed to fetch employees:', error);
        setEmployees([]);
      }
    };
    
    fetchEmployees();
  }, []);

  // Refresh employees function (can be called externally)
  const refreshData = useCallback(async () => {
    try {
      // Get employees using the service provider
      const data = await employeeService.getEmployees();
      if (Array.isArray(data)) {
        const formattedEmployees = data.map(formatEmployeeRecord).filter(Boolean);
        setEmployees(formattedEmployees);
        console.log(`Refreshed ${data.length} employees from database`);
      } else {
        console.log('No employees found during refresh or invalid data format.');
        setEmployees([]);
      }
    } catch (error) {
      console.error('Error refreshing employees:', error);
      setEmployees([]);
    }
  }, []);

  // Listen for refresh events from header
  useEffect(() => {
    const handleRefresh = () => {
      console.log('Employees context received refresh event');
      refreshData();
    };

    // Listen for general refresh events
    window.addEventListener('refresh-employees', handleRefresh);
    window.addEventListener('refresh-all-data', handleRefresh);
    window.addEventListener(HR_EMPLOYEES_REFRESH_EVENT, handleRefresh);

    return () => {
      window.removeEventListener('refresh-employees', handleRefresh);
      window.removeEventListener('refresh-all-data', handleRefresh);
      window.removeEventListener(HR_EMPLOYEES_REFRESH_EVENT, handleRefresh);
    };
  }, [refreshData]);

  // Employee management methods
  const addEmployee = async (employee) => {
    try {
      // Ensure required fields are present
      if (!employee.firstName || !employee.lastName) {
        throw new Error(em('name_required'));
      }

      // Prepare employee data - ensure all fields match server-side expectations
      const generatedId = Date.now().toString();
      
      // Default employee ID if not provided
      const employeeId = employee.employeeId || `EMP${(employees.length + 1).toString().padStart(3, '0')}`;
      
      // Ensure we use correct date format for hire date
      const hireDate = employee.hireDate || new Date().toISOString().split('T')[0];
      
      // Create a simplified payload with only the necessary fields
      // This reduces the chance of issues with field name inconsistencies
      const newEmployee = {
        id: generatedId,
        first_name: employee.firstName,
        last_name: employee.lastName,
        email: employee.email || '',
        phone: employee.phone || '',
        address: employee.address || '',
        city: employee.city || '',
        postal_code: employee.postalCode || '',
        position: employee.position || '',
        department: employee.department || '',
        salary: Number(employee.salary) || 0,
        hire_date: hireDate,
        status: employee.status || 'active',
        employee_id: employeeId,
        work_type: employee.workType || 'full-time',
        // Also update employee_type for backwards compatibility
        employee_type: employee.workType || 'full-time',
        skills: JSON.stringify(employee.skills || [])
      };
      
      console.log('Creating employee with data:', newEmployee);
      
      // Try to save using service provider
      const createdEmployee = await employeeService.createEmployee(newEmployee);
      
      // Map the server response back to our frontend format
      const formattedEmployee = {
        id: createdEmployee.id || generatedId,
        firstName: createdEmployee.firstName || createdEmployee.first_name,
        lastName: createdEmployee.lastName || createdEmployee.last_name,
        email: createdEmployee.email,
        phone: createdEmployee.phone,
        address: createdEmployee.address,
        city: createdEmployee.city,
        postalCode: createdEmployee.postal_code || createdEmployee.postalCode,
        position: createdEmployee.position,
        employeeId: createdEmployee.employeeId || createdEmployee.employee_id,
        workType: createdEmployee.workType || createdEmployee.work_type || 'full-time',
        status: createdEmployee.status || (createdEmployee.active ? 'active' : 'inactive'),
        salary: createdEmployee.salary || 0,
        department: createdEmployee.department || '',
        hireDate: createdEmployee.hireDate || createdEmployee.hire_date || hireDate,
        skills: createdEmployee.skills || []
      };
      
      // Update local state
      const updatedEmployees = [...employees, formattedEmployee];
      setEmployees(updatedEmployees);
      
      // Emit events
      eventEmitter.emit(EVENT_TYPES.EMPLOYEE_CREATED, formattedEmployee);
      eventEmitter.emit(EVENT_TYPES.DATA_CHANGED, 'employees');
      
      return formattedEmployee;
    } catch (error) {
      console.error('Error adding employee:', error);
      throw error;
    }
  };

  const updateEmployee = async (id, updatedEmployee) => {
    try {
      const oldEmployee = employees.find(employee => employee.id === id);
      if (!oldEmployee) {
        throw new Error(em('not_found', { id }));
      }
      
      // Prepare employee data for API - convert from frontend format to backend format
      const employeeData = {
        first_name: updatedEmployee.firstName || oldEmployee.firstName,
        last_name: updatedEmployee.lastName || oldEmployee.lastName,
        email: updatedEmployee.email || oldEmployee.email,
        phone: updatedEmployee.phone || oldEmployee.phone,
        address: updatedEmployee.address || oldEmployee.address,
        city: updatedEmployee.city || oldEmployee.city,
        position: updatedEmployee.position || oldEmployee.position,
        employee_id: updatedEmployee.employeeId || oldEmployee.employeeId,
        work_type: updatedEmployee.workType || oldEmployee.workType || 'full-time',
        // legacy alias
        employee_type: updatedEmployee.workType || oldEmployee.workType || 'full_time',
        status: updatedEmployee.status || oldEmployee.status || 'active',
        salary: updatedEmployee.salary || oldEmployee.salary || 0,
        department: updatedEmployee.department || oldEmployee.department || '',
        active: updatedEmployee.status === 'inactive' ? false : true
      };
      
      // Try to update using service provider
      const updatedData = await employeeService.updateEmployee(id, employeeData);
      
      // Map the server response back to our frontend format
      const formattedEmployee = {
        ...oldEmployee,
        ...updatedEmployee,
        id: updatedData.id || id,
        firstName: updatedData.firstName || updatedData.first_name,
        lastName: updatedData.lastName || updatedData.last_name,
        email: updatedData.email,
        phone: updatedData.phone,
        address: updatedData.address,
        city: updatedData.city,
        position: updatedData.position,
        employeeId: updatedData.employeeId || updatedData.employee_id,
        workType: updatedData.workType || updatedData.work_type || updatedData.employee_type || oldEmployee.workType || 'full-time',
        status: updatedData.status || (updatedData.active ? 'active' : 'inactive'),
        salary: updatedData.salary || oldEmployee.salary || 0,
        department: updatedData.department || oldEmployee.department || '',
        active: updatedData.active === 1 || updatedData.active === true
      };
      
      // Update local state
      const newEmployees = employees.map(employee => 
        employee.id === id ? formattedEmployee : employee
      );
      
      setEmployees(newEmployees);
      
      // Emit events
      eventEmitter.emit(EVENT_TYPES.EMPLOYEE_UPDATED, {
        oldEmployee,
        newEmployee: formattedEmployee
      });
      eventEmitter.emit(EVENT_TYPES.DATA_CHANGED, 'employees');
      
      return formattedEmployee;
    } catch (error) {
      console.error('Error updating employee:', error);
      throw error;
    }
  };

  const deleteEmployee = async (id) => {
    try {
      const employeeToDelete = employees.find(employee => employee.id === id);
      if (!employeeToDelete) {
        console.warn(`⚠️ Employee with id ${id} not found in local state - may have been already deleted`);
        return { success: true, alreadyDeleted: true };
      }
      
      console.log(`🗑️ Deleting employee: ${employeeToDelete.firstName} ${employeeToDelete.lastName} (${id})`);
      
      // Try to delete using service provider
      try {
        await employeeService.deleteEmployee(id);
        console.log(`✅ Employee ${id} successfully deleted from database`);
      } catch (apiError) {
        // If it's a 404 error, the employee was already deleted from database
        if (apiError.message && apiError.message.includes('not found')) {
          console.warn(`⚠️ Employee ${id} was already deleted from database, updating local state only`);
        } else {
          throw apiError; // Re-throw other errors
        }
      }
      
      // Update local state (remove from employees list)
      const newEmployees = employees.filter(employee => employee.id !== id);
      setEmployees(newEmployees);
      
      // Emit events
      eventEmitter.emit(EVENT_TYPES.EMPLOYEE_DELETED, employeeToDelete);
      eventEmitter.emit(EVENT_TYPES.DATA_CHANGED, 'employees');
      
      console.log(`🎉 Employee ${id} deletion completed successfully`);
      return { success: true };
    } catch (error) {
      console.error(`❌ Error deleting employee ${id}:`, error);
      throw error;
    }
  };

  const getEmployeeById = (id) => {
    return employees.find(employee => employee.id === id);
  };

  const getActiveEmployees = () => {
    return employees.filter(employee => employee.status === 'active');
  };

  const getDepartments = () => {
    return [...new Set(employees.map(e => e.department))].filter(Boolean);
  };

  const getPositions = () => {
    return [...new Set(employees.map(e => e.position))].filter(Boolean);
  };

  const getManagerOptions = () => {
    return employees
      .filter(e => e.status === 'active' && (e.position.toLowerCase().includes('manager') || 
                                            e.position.toLowerCase().includes('chef') || 
                                            e.position.toLowerCase().includes('directeur') ||
                                            e.position === 'Direction'))
      .map(e => `${e.firstName} ${e.lastName}`);
  };

  const getTotalSalaries = () => {
    return employees
      .filter(e => e.status === 'active')
      .reduce((sum, e) => sum + (Number(e.salary) || 0), 0);
  };

  return (
    <EmployeesContext.Provider value={{
      employees,
      addEmployee,
      updateEmployee,
      deleteEmployee,
      getEmployeeById,
      getActiveEmployees,
      getDepartments,
      getPositions,
      getManagerOptions,
      getTotalSalaries,
      refreshData
    }}>
      {children}
    </EmployeesContext.Provider>
  );
}; 

